// react
import { ChangeEvent, Dispatch, MouseEvent, SetStateAction, useContext, useState } from 'react';

// types
import { ErrorCause, Feedback } from '../../../@types';

// router
import { useNavigate } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// mui
import { Alert, AlertTitle, Grid, SelectChangeEvent } from '@mui/material';

// fields
import { Input } from '../../fields/Input/Input';
import { SelectCurrency } from '../../fields/SelectCurrency/SelectCurrency';

// props
export interface FormAddIfrPlanValues {
	currency: string;
	dateFiscalYearEnd: string;
	dateFiscalYearStart: string;
	name: string;
}

export interface FormAddIfrPlanProps {
	hasNavigate?: boolean;
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading?: Dispatch<SetStateAction<boolean>>;
}

export const FormAddIfrPlan = ({
	hasNavigate = true,
	onClose,
	onSuccess,
	setIsLoading,
}: FormAddIfrPlanProps) => {
	// hooks
	const navigate = useNavigate();
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);

	// Get reporting currency
	const currencyReporting =
		project?.currencies?.find((currency) => currency.type === 'REPORTING') || null;

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// defaults
	const valuesDefaults = {
		currency: currencyReporting?.currency || '',
		dateFiscalYearEnd: '',
		dateFiscalYearStart: '',
		name: '',
	};

	// set default fiscal year using project
	if (project?.fiscalYearStart && project?.fiscalYearEnd) {
		const dateToday = new Date();
		const dateTodayYear = dateToday.getFullYear();

		let endYear = dateTodayYear;

		const fiscalYearEndArray = project.fiscalYearEnd.split('-');
		const fiscalYearStartArray = project.fiscalYearStart.split('-');

		// increment end year if end month is before start month
		if (fiscalYearEndArray[0] < fiscalYearStartArray[0]) {
			endYear++;
		}

		valuesDefaults.dateFiscalYearEnd = `${endYear}-${project.fiscalYearEnd}`;
		valuesDefaults.dateFiscalYearStart = `${dateTodayYear}-${project.fiscalYearStart}`;
	}

	// state
	const [values, setValues] = useState<FormAddIfrPlanValues>(valuesDefaults);
	const [feedback, setFeedback] = useState<Feedback | null>(null);

	// vars
	const idForm = 'form-add-plan';

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		try {
			// set loader
			if (setIsLoading) {
				setIsLoading(true);
			}

			const fetchAddPlan = await fetch(`${process.env.REACT_APP_API_URL}/ifrs/plans`, {
				method: 'POST',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
				body: JSON.stringify({
					currency: values.currency,
					dateFiscalYearEnd: values.dateFiscalYearEnd,
					dateFiscalYearStart: values.dateFiscalYearStart,
					name: values.name,
					project: project?.id,
				}),
			});
			const resAddPlan = await fetchAddPlan.json();

			if (resAddPlan.error) {
				throw new Error(resAddPlan.error.message, {
					cause: {
						id: resAddPlan.error.id,
					},
				});
			}

			const ifrPlanNew = resAddPlan.data;

			// success
			onSuccess?.();

			if (hasNavigate) {
				navigate(`/project/${project?.idWbg}/plan/${ifrPlanNew.id}`);
			} else {
				// set success banner
				setBanner({
					hasClose: true,
					message: t('alert.ifrPlanCreated', { name: values.name }),
					severity: 'success',
				});

				// close
				onClose?.();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			const feedbackNew = {
				alert: {
					children: t('error.default'),
				},
			};

			if (cause) {
				if (cause.id === 'IFR_PLAN_DUPLICATE') {
					feedbackNew.alert.children = t('error.ifrPlanDuplicate');
				} else if (cause.id === 'IFR_PLAN_FISCAL_YEAR_DUPLICATE') {
					feedbackNew.alert.children = t('error.ifrPlanFiscalYearDuplicate', {
						year: new Date(values.dateFiscalYearEnd).getFullYear(),
					});
				} else if (cause.id === 'IFR_PLAN_UNAUTHORIZED_POST') {
					feedbackNew.alert.children = t('error.unauthorized');
				}
			}

			// set feedback
			setFeedback(feedbackNew);
		} finally {
			// set loader
			if (setIsLoading) {
				setIsLoading(false);
			}
		}
	};

	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues({
			...values,
			[name]: value,
		});
	};

	return (
		<form id={idForm} name="formAddPlan" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-name`}
						label={t('name')}
						name="name"
						onChange={handleChangeValue}
						required={true}
						value={values.name}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<SelectCurrency
						currencies={[...(new Set(project?.currencies?.map((c) => c.currency)) || [])]}
						id={`${idForm}-currency`}
						name="currency"
						onChange={handleChangeValue}
						required={true}
						value={values.currency}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-fiscal-year-start`}
						inputProps={{
							max: values.dateFiscalYearEnd,
						}}
						label={t('fiscalYearStart')}
						name="dateFiscalYearStart"
						type="date"
						onChange={handleChangeValue}
						required
						value={values.dateFiscalYearStart}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-fiscal-year-end`}
						inputProps={{
							min: values.dateFiscalYearStart,
						}}
						label={t('fiscalYearEnd')}
						name="dateFiscalYearEnd"
						type="date"
						onChange={handleChangeValue}
						required
						value={values.dateFiscalYearEnd}
					/>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</Grid>
		</form>
	);
};
